<template>
  <BaseDialogForm :is-edit="isEdit">
    <template v-slot:toggle="{ on }">
      <v-btn v-if="!isEdit" color="primary" v-on="on" @click.stop="onOpen()">
        <v-icon left>
          add_box
        </v-icon>
        New Material
      </v-btn>
      <v-btn v-else icon v-on="on" @click.stop="onOpen()">
        <v-icon>
          edit
        </v-icon>
      </v-btn>
    </template>
    <template v-slot:title>
      <h2 v-if="isEdit">Edit {{ instance.name }}</h2>
      <h2 v-else>Make A New Material</h2>
    </template>
    <template v-slot:content>
      <v-form v-model="$v.valid" @submit.prevent="submit">
        <v-text-field
          v-model.trim="$v.formdata.name.$model"
          :error-messages="nameErrors"
          label="Material Name"
          counter="128"
          hint="Keep the material names unique"
          :prepend-icon="formdata.icon"
          @input="$v.formdata.name.$touch()"
          @blur="$v.formdata.name.$touch()"
        />
        <v-autocomplete
          v-model="$v.formdata.supplier_id.$model"
          :error-messages="supplierErrors"
          label="Supplier"
          :items="suppliers"
          :loading="supplierLoading"
          item-value="id"
          item-text="name"
          hint="Where we're getting the material from"
          prepend-icon="add_box"
          @click:prepend="showSupplierForm = !showSupplierForm"
          :append-icon="formdata.supplierIcon"
          @input="$v.formdata.supplier_id.$touch()"
          @blur="$v.formdata.supplier_id.$touch()"
        />
        <v-row justify="center">
          <v-col sm="8" cols="8">
            <SupplierFormNew v-show="showSupplierForm" />
          </v-col>
        </v-row>
        <v-autocomplete
          v-model="$v.formdata.manufacturer_id.$model"
          :error-messages="manufacturerErrors"
          label="Manufacturer"
          :items="manufacturers"
          :loading="manufacturerLoading"
          item-value="id"
          item-text="name"
          hint="The original source of the material"
          prepend-icon="add_box"
          @click:prepend="showManufacturerForm = !showManufacturerForm"
          :append-icon="formdata.manufacturerIcon"
          @input="$v.formdata.manufacturer_id.$touch()"
          @blur="$v.formdata.manufacturer_id.$touch()"
        />
        <v-row justify="center">
          <v-col sm="8" cols="8">
            <ManufacturerFormNew v-show="showManufacturerForm" />
          </v-col>
        </v-row>
        <v-autocomplete
          v-model="$v.formdata.powder_id.$model"
          :error-messages="powderErrors"
          label="Powder Type"
          :items="powders"
          :loading="powderLoading"
          item-value="id"
          item-text="name"
          hint="The type of powder...NCA, Graphite, Extrudate, etc."
          prepend-icon="add_box"
          @click:prepend="showPowderForm = !showPowderForm"
          :append-icon="formdata.powderIcon"
          @input="$v.formdata.powder_id.$touch()"
          @blur="$v.formdata.powder_id.$touch()"
        />
        <v-row justify="center">
          <v-col sm="8" cols="8">
            <PowderFormNew v-show="showPowderForm" />
          </v-col>
        </v-row>
        <v-text-field
          v-model="$v.formdata.surfacearea.$model"
          :error-messages="surfaceareaErrors"
          label="Surface Area"
          counter="9"
          suffix="m&sup2/g"
          hint="Use BET to get this value"
          type="number"
          :prepend-icon="formdata.surfaceareaIcon"
          @input="$v.formdata.surfacearea.$touch()"
          @blur="$v.formdata.surfacearea.$touch()"
        />
        <v-text-field
          v-model="$v.formdata.bulkdensity.$model"
          :error-messages="bulkdensityErrors"
          label="Bulk Density"
          counter="9"
          suffix="g/mL"
          hint="Can be tap OR bulk"
          type="Number"
          :prepend-icon="formdata.bulkdensityIcon"
          @input="$v.formdata.bulkdensity.$touch()"
          @blur="$v.formdata.bulkdensity.$touch()"
        />
        <v-text-field
          v-model="$v.formdata.size.$model"
          :error-messages="sizeErrors"
          label="D50 Size"
          counter="9"
          type="Number"
          :prepend-icon="formdata.sizeIcon"
          @input="$v.formdata.size.$touch()"
          @blur="$v.formdata.size.$touch()"
        />
        <v-text-field
          v-model="$v.formdata.moisture.$model"
          :error-messages="moistureErrors"
          label="Moisture"
          counter="9"
          type="Number"
          suffix="ppm"
          :prepend-icon="formdata.moistureIcon"
          @input="$v.formdata.moisture.$touch()"
          @blur="$v.formdata.moisture.$touch()"
        />
        <v-textarea
          v-model="formdata.notes"
          label="Extra Notes"
          prepend-icon="note"
        >
        </v-textarea>
        <v-switch
          v-model="formdata.customersupplied"
          label="Customer Supplied"
          hint="Check this if the material belongs to the client/customer"
        />
      </v-form>
    </template>

    <template #actions="{ close }">
      <v-btn color="warning" dark @click="clear">
        <v-icon left>fas fa-redo</v-icon>
        Clear
      </v-btn>
      <v-btn
        :disabled="$v.$invalid"
        color="primary"
        @click="
          submit();
          close();
        "
      >
        <v-icon dark left>
          fas fa-save
        </v-icon>
        Save
      </v-btn>
    </template>
  </BaseDialogForm>
</template>

<script>
import { validationMixin } from "vuelidate";
import { handleErrors } from "../../services/formErrors";
import Powder from "../../models/Powder";
import Supplier from "../../models/Supplier";
import Manufacturer from "../../models/Manufacturer";
import Material from "../../models/Material";
import BaseDialogForm from "../Base/BaseDialogForm";
import PowderFormNew from "./PowderFormNew";
import SupplierFormNew from "./SupplierFormNew";
import ManufacturerFormNew from "./ManufacturerFormNew";
export default {
  name: "MaterialForm",
  components: {
    BaseDialogForm,
    PowderFormNew,
    SupplierFormNew,
    ManufacturerFormNew
  },
  mixins: [validationMixin],
  validations: Material.validations,
  props: {
    instance: {
      type: Object,
      default: () => {}
    },
    isEdit: {
      type: Boolean,
      required: true,
      default: false
    }
  },
  data() {
    return {
      manufacturerLoading: true,
      supplierLoading: true,
      powderLoading: true,
      formdata: new Material(this.instance),
      showSupplierForm: false,
      showManufacturerForm: false,
      showPowderForm: false
    };
  },
  computed: {
    // return error messages for validated fields
    nameErrors() {
      return handleErrors("formdata", "name", this);
    },
    supplierErrors() {
      return handleErrors("formdata", "supplier_id", this);
    },
    manufacturerErrors() {
      return handleErrors("formdata", "manufacturer_id", this);
    },
    powderErrors() {
      return handleErrors("formdata", "powder_id", this);
    },
    surfaceareaErrors() {
      return handleErrors("formdata", "surfacearea", this);
    },
    bulkdensityErrors() {
      return handleErrors("formdata", "bulkdensity", this);
    },
    sizeErrors() {
      return handleErrors("formdata", "size", this);
    },
    moistureErrors() {
      return handleErrors("formdata", "moisture", this);
    },
    // Wait to render form until this completes
    loading() {
      return (
        this.powderLoading || this.supplierLoading || this.manufacturerLoading
      );
    },
    // For location selection field
    powders() {
      return Powder.query().orderBy('name').get();
    },
    suppliers() {
      return Supplier.query().orderBy('name').get();
    },
    manufacturers() {
      return Manufacturer.query().orderBy('name').get();
    }
  },
  methods: {
    onOpen() {
      this.initialize();
    },
    initialize() {
      Powder.$fetch().then(() => (this.powderLoading = false));
      Supplier.$fetch().then(() => (this.supplierLoading = false));
      Manufacturer.$fetch().then(() => (this.manufacturerLoading = false));
    },
    submit() {
      this.$v.$touch();
      if (this.isEdit) {
        Material.$put(this.instance.id, this.formdata.toUpdatePayload())
      } else {
        Material
          .$post(this.formdata.toCreatePayload())
          .then(() => this.clear())
      }
    },
    clear() {
      this.formdata = new Material();
      this.$v.$reset();
    }
  }
};
</script>

